var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Row, Col, Button, Modal, ModalFooter, MessageBanner, RadioButton, Icon, } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { RecekTextField, RecekOnixItemPicker, RecekSelection } from "../inputs";
import { PersonLegalFormEnum } from "../../types/Api";
import { usePersonOptions, buildPersonOptionLabel, } from "../../hooks/title/usePersonOptions";
import { useOnixOptions } from "../../hooks/title/useOnixOptions";
import { usePermissions } from "../../hooks/usePermissions";
import { RoleEnum } from "../../types/RoleEnum";
import { CreatePersonInlineModal } from "./CreatePersonInlineModal";
import css from "./ContributionFormModal.module.css";
var DEFAULT_CONTRIBUTION_ROLE_VALUE = "A01";
var legalFormForPersonType = function (type) {
    if (type === "natural")
        return PersonLegalFormEnum.NATURAL;
    if (type === "legal")
        return PersonLegalFormEnum.LEGAL;
    return undefined;
};
var applyPersonToDraft = function (prev, source) {
    var _a, _b, _c, _d;
    var next = __assign(__assign({}, prev), { person: source });
    if (source.legalForm === PersonLegalFormEnum.NATURAL) {
        next.firstName = (_a = source.firstName) !== null && _a !== void 0 ? _a : "";
        next.lastName = (_b = source.lastName) !== null && _b !== void 0 ? _b : "";
        next.biographicalNote = (_c = source.biographicalNote) !== null && _c !== void 0 ? _c : "";
        next.name = "";
    }
    else {
        next.name = (_d = source.name) !== null && _d !== void 0 ? _d : "";
        next.firstName = "";
        next.lastName = "";
    }
    return next;
};
var ContributionFormModal = function (_a) {
    var _b, _c;
    var open = _a.open, isEditing = _a.isEditing, draft = _a.draft, onDraftChange = _a.onDraftChange, initialPersonType = _a.initialPersonType, onSave = _a.onSave, onClose = _a.onClose, onAddRole = _a.onAddRole, onRemoveRole = _a.onRemoveRole, onUpdateRole = _a.onUpdateRole;
    var formatMessage = useIntl().formatMessage;
    var hasAnyRole = usePermissions().hasAnyRole;
    var canCreatePerson = hasAnyRole([
        RoleEnum.AGENCY_WORKER,
        RoleEnum.PUBLISHER_GUARANTOR,
        RoleEnum.PUBLISHER_ADMIN,
    ]);
    var _d = usePersonOptions(), personOptions = _d.personOptions, setPersonOptions = _d.setPersonOptions, onPersonFilter = _d.onPersonFilter, findPersonById = _d.findPersonById, clearPersonFilter = _d.clearPersonFilter, personOptionsLoading = _d.personOptionsLoading;
    var _e = useOnixOptions(), unnamedPersonTable = _e.unnamedPersonTable, unnamedPersonLoading = _e.unnamedPersonLoading, unnamedPersonStatus = _e.unnamedPersonStatus, contributionRoleTable = _e.contributionRoleTable, contributionRoleLoading = _e.contributionRoleLoading, contributionRoleStatus = _e.contributionRoleStatus;
    var _f = useState(initialPersonType), personType = _f[0], setPersonType = _f[1];
    var _g = useState(null), error = _g[0], setError = _g[1];
    var _h = useState(false), createPersonOpen = _h[0], setCreatePersonOpen = _h[1];
    var defaultRolePrefilled = useRef(false);
    useEffect(function () {
        if (open) {
            setPersonType(initialPersonType);
            setError(null);
            setPersonOptions([]);
            setCreatePersonOpen(false);
            defaultRolePrefilled.current = false;
        }
    }, [open, initialPersonType, setPersonOptions]);
    useEffect(function () {
        setCreatePersonOpen(false);
    }, [personType]);
    var handleCreatedPerson = useCallback(function (created) {
        if (!created.id)
            return;
        var source = created;
        clearPersonFilter();
        onDraftChange(function (prev) { return applyPersonToDraft(prev, source); });
        setPersonOptions([
            {
                value: source.id,
                label: buildPersonOptionLabel(source) || source.id,
            },
        ]);
        setCreatePersonOpen(false);
    }, [clearPersonFilter, onDraftChange, setPersonOptions]);
    var prefillDefaultRole = useCallback(function () {
        if (defaultRolePrefilled.current || isEditing)
            return;
        var items = contributionRoleTable === null || contributionRoleTable === void 0 ? void 0 : contributionRoleTable.onixCodetableItems;
        if (!items)
            return;
        var defaultRole = items.find(function (item) { return item.value === DEFAULT_CONTRIBUTION_ROLE_VALUE; });
        if (defaultRole) {
            onAddRole(defaultRole);
            defaultRolePrefilled.current = true;
        }
    }, [isEditing, contributionRoleTable, onAddRole]);
    useEffect(function () {
        if (open)
            prefillDefaultRole();
    }, [open, prefillDefaultRole]);
    useEffect(function () {
        var _a;
        if (!open || !((_a = draft.person) === null || _a === void 0 ? void 0 : _a.id))
            return;
        var id = draft.person.id;
        var label = buildPersonOptionLabel(draft.person);
        setPersonOptions([{ value: id, label: label || id }]);
    }, [open, draft.person, setPersonOptions]);
    var handlePersonTypeChange = function (type) {
        setPersonType(type);
        setError(null);
        setPersonOptions([]);
        clearPersonFilter();
        onDraftChange(function (prev) { return (__assign(__assign({}, prev), { person: undefined, firstName: "", lastName: "", biographicalNote: "", name: "", unnamedPerson: undefined })); });
    };
    var handlePersonFilter = function (nameFilter) {
        onPersonFilter(nameFilter, legalFormForPersonType(personType));
    };
    var handlePersonChange = function (personId) {
        var selected = findPersonById(personId);
        if (!selected)
            return;
        onDraftChange(function (prev) { return applyPersonToDraft(prev, selected); });
    };
    var handleUnnamedPersonChange = function (item) {
        onDraftChange(function (prev) { return (__assign(__assign({}, prev), { unnamedPerson: item })); });
    };
    var handleSave = function () {
        var person = draft.person, unnamedPerson = draft.unnamedPerson, roles = draft.roles;
        if (personType === "unnamed") {
            if (!unnamedPerson) {
                setError(formatMessage({
                    id: "ui-br-agency.title.contributions.form.error.requiredContributionField",
                }));
                return;
            }
        }
        else if (!(person === null || person === void 0 ? void 0 : person.id)) {
            setError(formatMessage({
                id: "ui-br-agency.title.contributions.form.error.requiredContributionField",
            }));
            return;
        }
        if (!roles || roles.length === 0) {
            setError(formatMessage({
                id: "ui-br-agency.title.contributions.form.error.rolesRequired",
            }));
            return;
        }
        setError(null);
        onSave();
    };
    var handleClose = function () {
        setError(null);
        onClose();
    };
    var modalLabel = isEditing
        ? formatMessage({
            id: "ui-br-agency.title.contributions.form.edit.title",
        })
        : formatMessage({
            id: "ui-br-agency.title.contributions.form.add.title",
        });
    var footer = (React.createElement(ModalFooter, null,
        React.createElement(Button, { buttonStyle: "primary", onClick: handleSave },
            React.createElement(FormattedMessage, { id: "ui-br-agency.button.save" })),
        React.createElement(Button, { buttonStyle: "default", onClick: handleClose },
            React.createElement(FormattedMessage, { id: "ui-br-agency.button.cancel" }))));
    var isNatural = personType === "natural";
    var isLegal = personType === "legal";
    var isUnnamed = personType === "unnamed";
    var personLoadingMessage = (React.createElement(Icon, { icon: "spinner-ellipsis" }, formatMessage({
        id: "ui-br-agency.title.contributions.form.person.searching",
    })));
    return (React.createElement(Modal, { open: open, onClose: handleClose, label: modalLabel, footer: footer, size: "large", dismissible: true },
        error && (React.createElement(MessageBanner, { type: "error", className: css.errorBanner }, error)),
        React.createElement(Row, null,
            React.createElement(Col, { xs: 12 },
                React.createElement("fieldset", { className: css.personTypeFieldset },
                    React.createElement("legend", { className: css.personTypeLegend },
                        React.createElement(FormattedMessage, { id: "ui-br-agency.title.contributions.form.personType" })),
                    React.createElement("div", { className: css.personTypeOptions },
                        React.createElement(RadioButton, { label: formatMessage({
                                id: "ui-br-agency.title.contributions.form.personType.natural",
                            }), checked: isNatural, onChange: function () { return handlePersonTypeChange("natural"); } }),
                        React.createElement(RadioButton, { label: formatMessage({
                                id: "ui-br-agency.title.contributions.form.personType.legal",
                            }), checked: isLegal, onChange: function () { return handlePersonTypeChange("legal"); } }),
                        React.createElement(RadioButton, { label: formatMessage({
                                id: "ui-br-agency.title.contributions.form.personType.unnamed",
                            }), checked: isUnnamed, onChange: function () { return handlePersonTypeChange("unnamed"); } }))))),
        !isUnnamed && (React.createElement(Row, null,
            React.createElement(Col, { xs: 3 },
                React.createElement(RecekSelection, { labelId: isLegal
                        ? "ui-br-agency.title.contributions.form.legalPerson"
                        : "ui-br-agency.title.contributions.form.person", dataOptions: personOptions, asyncFilter: true, onFilter: handlePersonFilter, value: ((_b = draft.person) === null || _b === void 0 ? void 0 : _b.id) || "", onChange: handlePersonChange, loading: personOptionsLoading, loadingMessage: personLoadingMessage })),
            canCreatePerson && (React.createElement(Col, { xs: 3, className: css.createPersonButtonCol },
                React.createElement(Button, { buttonStyle: "default", marginBottom0: true, onClick: function () { return setCreatePersonOpen(true); } },
                    React.createElement(FormattedMessage, { id: "ui-br-agency.title.contributions.form.createNewPerson" })))),
            isNatural && (React.createElement(React.Fragment, null,
                React.createElement(Col, { xs: 3 },
                    React.createElement(RecekTextField, { labelId: "ui-br-agency.title.contributions.form.firstName", value: draft.firstName || "", readOnly: true })),
                React.createElement(Col, { xs: 3 },
                    React.createElement(RecekTextField, { labelId: "ui-br-agency.title.contributions.form.lastName", value: draft.lastName || "", readOnly: true })))))),
        isLegal && (React.createElement(Row, null,
            React.createElement(Col, { xs: 6 },
                React.createElement(RecekTextField, { labelId: "ui-br-agency.title.contributions.form.organizationName", value: draft.name || "", readOnly: true })))),
        isUnnamed && (React.createElement(Row, null,
            React.createElement(Col, { xs: 6, className: css.unnamedPersonPicker },
                React.createElement(RecekOnixItemPicker, { labelId: "ui-br-agency.title.contributions.form.unnamedPerson", dataOptions: (_c = unnamedPersonTable === null || unnamedPersonTable === void 0 ? void 0 : unnamedPersonTable.onixCodetableItems) !== null && _c !== void 0 ? _c : [], value: draft.unnamedPerson, onChange: handleUnnamedPersonChange, loading: unnamedPersonLoading, codetableStatus: unnamedPersonStatus })))),
        isNatural && (React.createElement(Row, null,
            React.createElement(Col, { xs: 6 },
                React.createElement(RecekTextField, { labelId: "ui-br-agency.title.contributions.form.biographicalNote", value: draft.biographicalNote || "", onChange: function (e) {
                        return onDraftChange(function (prev) { return (__assign(__assign({}, prev), { biographicalNote: e.target.value })); });
                    } })))),
        React.createElement(Row, null,
            React.createElement(Col, { xs: 12 },
                React.createElement("h5", null,
                    React.createElement(FormattedMessage, { id: "ui-br-agency.title.contributions.form.roles" }),
                    React.createElement("span", { className: css.requiredMark }, " *")),
                (draft.roles || []).map(function (roleItem, roleIndex) {
                    var _a;
                    return (React.createElement(Row, { key: roleIndex, className: css.roleRow },
                        React.createElement(Col, { xs: 10 },
                            React.createElement(RecekOnixItemPicker, { labelId: "ui-br-agency.title.contributions.form.role", dataOptions: (_a = contributionRoleTable === null || contributionRoleTable === void 0 ? void 0 : contributionRoleTable.onixCodetableItems) !== null && _a !== void 0 ? _a : [], value: roleItem.role, onChange: function (item) {
                                    if (item)
                                        onUpdateRole(roleIndex, item);
                                }, loading: contributionRoleLoading, codetableStatus: contributionRoleStatus })),
                        React.createElement(Col, { xs: 2 },
                            React.createElement(Button, { buttonStyle: "danger", marginBottom0: true, onClick: function () { return onRemoveRole(roleIndex); } },
                                React.createElement(FormattedMessage, { id: "ui-br-agency.button.remove" })))));
                }),
                React.createElement(Button, { buttonStyle: "default", onClick: function () { var _a; return onAddRole((_a = contributionRoleTable === null || contributionRoleTable === void 0 ? void 0 : contributionRoleTable.onixCodetableItems) === null || _a === void 0 ? void 0 : _a[0]); } },
                    React.createElement(FormattedMessage, { id: "ui-br-agency.title.contributions.form.addRole" })))),
        !isUnnamed && canCreatePerson && (React.createElement(CreatePersonInlineModal, { open: createPersonOpen, personType: isLegal ? "legal" : "natural", onClose: function () { return setCreatePersonOpen(false); }, onCreated: handleCreatedPerson }))));
};
export { ContributionFormModal };
